// apps/roleforge-app/src/components/audit/ActivationMomentLayout.tsx
//
// Plan 3e-ii Task 5.7 — The Activation Moment screen, rendered in the
// center column of the persistent shell when audit.status ===
// 'ready_to_materialize' (ShellMode === 'locked').
//
// Composition (top-down):
//   1. ActivationPositioningParagraph (Lamborghini-chassis copy)
//   2. Heading: "Your AI Workforce Blueprint is ready."
//   3. Three AiTeamPreviewTiles (data from `team`)
//   4. Year1SavingsLine
//   5. Primary CTA: [Set it up in VibeControl] -> /audits/:id/materialize
//   6. Secondary CTA: ExportAssetsLink ("Download my Blueprint Bundle")
//   7. Scrollable deliverables summary:
//        - OperatingModelPreview
//        - ImplementationRecommendations (uses opportunities prop)
//        - Top 3-5 ActivationMomentDeepDiveCards
//        - StarterSkillPack
//
// Hard constraint (§13): NO agency CTAs anywhere on this screen.

import { Link } from 'react-router-dom';
import type { AuditRow, Opportunity, RecommendationRow } from '../../lib/api-client';
import { useAuth } from '../../lib/auth';
import { ActivationPositioningParagraph } from './ActivationPositioningParagraph';
import { AiTeamPreviewTile } from './AiTeamPreviewTile';
import { Year1SavingsLine } from './Year1SavingsLine';
import { ExportAssetsLink } from './ExportAssetsLink';
import { ActivationMomentDeepDiveCard } from './ActivationMomentDeepDiveCard';
import { OperatingModelPreview } from '../deliverables/OperatingModelPreview';
import { ImplementationRecommendations } from '../deliverables/ImplementationRecommendations';
import { StarterSkillPack } from '../deliverables/StarterSkillPack';

const MAX_DEEP_DIVES = 5;
const MIN_QUICK_WINS = 3;

function selectTopOpps(opps: Opportunity[]): Opportunity[] {
  const apply = opps.filter((o) => o.applies);
  const quick = apply
    .filter((o) => o.quadrant === 'quick_wins')
    .sort((a, b) => b.business_impact - a.business_impact);
  const big = apply
    .filter((o) => o.quadrant === 'big_swings')
    .sort((a, b) => b.business_impact - a.business_impact);
  const out: Opportunity[] = [];
  for (const o of quick) {
    if (out.length >= MAX_DEEP_DIVES) break;
    out.push(o);
  }
  if (out.length < MIN_QUICK_WINS) {
    for (const o of big) {
      if (out.length >= MAX_DEEP_DIVES) break;
      out.push(o);
    }
  }
  return out.slice(0, MAX_DEEP_DIVES);
}

export interface ActivationMomentLayoutProps {
  auditId: string;
  team: { agentName: string; role: string; firstTask: string }[];
  roi: { floorCents: number; ceilingCents: number | null };
  opportunities: Opportunity[];
  engineLabels?: Record<string, string>;
  recommendations: RecommendationRow[];
  /**
   * The audit row drives `OperatingModelPreview` — when null, the preview
   * section is omitted (the audit data hasn't loaded yet). The shell wires a
   * loading placeholder for that case at the section level.
   */
  audit: AuditRow | null;
  /**
   * Reserved for a future starter-skills override. The current
   * `StarterSkillPack` derives its tools from the recommendations rows + the
   * skills catalog, so this prop is unused at render time but kept on the
   * interface so the page-side composer can plumb it without a follow-up
   * type change.
   */
  starterSkills: unknown[];
}

export function ActivationMomentLayout(props: ActivationMomentLayoutProps) {
  const { jwt } = useAuth();
  const top = selectTopOpps(props.opportunities);

  return (
    <div className="flex flex-col gap-6 p-6 max-w-3xl mx-auto">
      <ActivationPositioningParagraph />

      <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 text-center">
        Your AI Workforce Blueprint is ready.
      </h1>

      <section
        className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        data-testid="operations-manager-coordinator"
      >
        <p className="font-semibold text-slate-900 dark:text-slate-100">How your workforce runs</p>
        <p>One AI works every seat in this business — same AI, each seat&apos;s job, under each seat&apos;s access.</p>
      </section>

      <div className="flex gap-4">
        {props.team.slice(0, 3).map((t, i) => (
          <AiTeamPreviewTile key={i} {...t} />
        ))}
      </div>

      <Year1SavingsLine
        floorCents={props.roi.floorCents}
        ceilingCents={props.roi.ceilingCents}
      />

      <div className="flex flex-col items-center gap-3">
        <Link
          to={`/audits/${props.auditId}/materialize`}
          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Set it up in VibeControl
        </Link>
        <ExportAssetsLink auditId={props.auditId} />
      </div>

      <hr className="my-4 border-slate-200 dark:border-slate-700" />

      {props.audit && (
        <section data-testid="activation-operating-model">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">AI Workforce Blueprint</h3>
          <OperatingModelPreview audit={props.audit} />
        </section>
      )}

      <section data-testid="activation-recommendations">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">Implementation Recommendations</h3>
        <ImplementationRecommendations
          opportunities={props.opportunities}
          engineLabels={props.engineLabels}
        />
      </section>

      {top.length > 0 && (
        <section data-testid="activation-deep-dives">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">Top Opportunities</h3>
          <div className="flex flex-col gap-4">
            {top.map((o) => (
              <ActivationMomentDeepDiveCard key={o.id} opp={o} />
            ))}
          </div>
        </section>
      )}

      {jwt && (
        <section data-testid="activation-starter-skills">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">Starter Skill Pack</h3>
          <StarterSkillPack recommendations={props.recommendations} jwt={jwt} />
        </section>
      )}
    </div>
  );
}
